import React, { useEffect } from 'react';
import { TrendingUp, RefreshCw, DollarSign, Euro } from 'lucide-react';
import { useExchangeRates } from '../contexts/ExchangeRatesContext';

const ExchangeRatesWidget = () => {
  const { 
    rates, 
    loading, 
    refreshing, 
    lastUpdate, 
    initialized,
    loadRates,
    refreshRates 
  } = useExchangeRates();

  // Load rates if not initialized yet (after login)
  useEffect(() => {
    if (!initialized && !loading) {
      loadRates();
    }
  }, [initialized, loading, loadRates]);

  const formatRate = (rate) => {
    return rate ? rate.toFixed(4) : '-';
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRefresh = () => {
    refreshRates(true);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <TrendingUp size={20} />
            <h3 className="card-title">TCMB Döviz Kurları</h3>
          </div>
        </div>
        <div className="card-body" style={{ textAlign: 'center', padding: '2rem' }}>
          <RefreshCw size={24} className="animate-spin" style={{ color: '#64748b' }} />
          <p style={{ marginTop: '0.5rem', color: '#64748b' }}>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!rates || !rates.currencies) {
    return (
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <TrendingUp size={20} />
              <h3 className="card-title">TCMB Döviz Kurları</h3>
            </div>
            <button 
              className="btn btn-secondary" 
              onClick={handleRefresh}
              disabled={refreshing}
              style={{ padding: '0.5rem' }}
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        <div className="card-body" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
          Döviz kurları yüklenemedi
        </div>
      </div>
    );
  }

  const { USD, EUR } = rates.currencies;

  // Calculate EUR/USD cross rate
  const eurUsdCrossRate = USD && EUR ? (EUR.forex_buying / USD.forex_buying) : null;

  // Only USD and EUR
  const displayCurrencies = [
    { data: USD, icon: DollarSign, color: '#10b981', label: 'Dolar' },
    { data: EUR, icon: Euro, color: '#3b82f6', label: 'Euro' }
  ].filter(c => c.data);

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <TrendingUp size={20} />
            <h3 className="card-title">TCMB Döviz Kurları</h3>
          </div>
          <button 
            className="btn btn-secondary" 
            onClick={handleRefresh}
            disabled={refreshing}
            title="Kurları Yenile"
            style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            <span style={{ marginLeft: '0.5rem' }}>Yenile</span>
          </button>
        </div>
      </div>
      <div className="card-body" style={{ padding: '1.5rem' }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1rem' 
        }}>
          {displayCurrencies.map((currency, idx) => {
            const Icon = currency.icon;
            return (
              <div key={idx} style={{ 
                padding: '1rem', 
                background: '#f8fafc', 
                borderRadius: '8px',
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <Icon size={24} style={{ color: currency.color }} />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                      {currency.data.code} - {currency.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      {currency.data.unit > 1 ? `${currency.data.unit} ` : ''}{currency.data.name}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Alış</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#10b981' }}>
                      ₺{formatRate(currency.data.forex_buying)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Satış</div>
                    <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#ef4444' }}>
                      ₺{formatRate(currency.data.forex_selling)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* EUR/USD Cross Rate Card */}
          {eurUsdCrossRate && (
            <div style={{ 
              padding: '1rem', 
              background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', 
              borderRadius: '8px',
              border: '1px solid #bae6fd'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '1.5rem' }}>💱</span>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                    EUR/USD
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Cross Rate
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0369a1' }}>
                  {eurUsdCrossRate.toFixed(4)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                  1 EUR = {eurUsdCrossRate.toFixed(4)} USD
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer with last update time */}
        <div style={{ 
          marginTop: '1rem', 
          paddingTop: '1rem', 
          borderTop: '1px solid #e2e8f0',
          fontSize: '0.75rem',
          color: '#64748b',
          textAlign: 'center'
        }}>
          Son güncelleme: {formatDate(lastUpdate)}
          {rates.source && ` • Kaynak: ${rates.source}`}
        </div>
      </div>
    </div>
  );
};

export default ExchangeRatesWidget;
